import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput as RNTextInput,
  Image,
  Alert,
  Modal,
  Dimensions,
  ActivityIndicator,
  Platform,
  Animated,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../../types/navigation';
import {
  ColorPicker,
  SocialShareModal,
  LocationMap,
  LocationPicker,
} from '../../components/shared';
import { TextField, Button } from '../../components/common';
import MoodBoardPreviewCard from '../../components/custom/moodboard/MoodBoardPreviewCard';
import {
  VerticalMoodboardCard,
  ThumbnailCard,
  LayoutToggle,
  CardLayout,
  MoodBoardCardItem,
  MoodBoardCardActions,
  ImageNavigationProps,
} from '../../components/custom/moodboard';
import ImageUploader from '../../components/common/ImageUploader';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import {
  moodBoardAPI,
  MoodBoard,
  UpdateMoodBoardRequest,
} from '../../api/mood_boards';
import { useAuth } from '../../hooks/useAuth';
import { getToken } from '../../utils/storage';
import { API_URL } from '../../constants/config';
import axios from 'axios';
import { uriToBase64 } from '../../utils/imageUtils';
import { LinearGradient } from 'expo-linear-gradient';
import { hexToRgba } from '../../utils/colorUtils';
import { trackButtonClick, trackAnalyticsEvent } from '../../utils/analytics';

type MoodBoardDetailScreenNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;
type MoodBoardDetailScreenRouteProp = RouteProp<
  RootStackParamList,
  'MoodBoardDetail'
>;

const MoodBoardDetailScreen = () => {
  const navigation = useNavigation<MoodBoardDetailScreenNavigationProp>();
  const route = useRoute<MoodBoardDetailScreenRouteProp>();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { boardId, isEditing: initialIsEditing } = route.params;

  const isNavigatingAwayRef = React.useRef(false);

  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));

  const [moodBoard, setMoodBoard] = useState<MoodBoard | null>(null);
  const [loading, setLoading] = useState(boardId !== 'new');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(
    boardId === 'new' || initialIsEditing === true
  );

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagFieldTouched, setTagFieldTouched] = useState(false);
  const [extractedColors, setExtractedColors] = useState<{
    [imageUri: string]: string[];
  }>({});
  const [showPhotoColors, setShowPhotoColors] = useState(false);

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editColors, setEditColors] = useState<string[]>([]);
  const [editImages, setEditImages] = useState<string[]>([]);
  const [previewLayout, setPreviewLayout] = useState<CardLayout>('horizontal');
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Extract colors from existing images when editing starts
  useEffect(() => {
    if (isEditing && moodBoard && moodBoard.images.length > 0) {
      extractColorsFromExistingImages();
    }
  }, [isEditing, moodBoard]);

  // Reset preview image index when images change
  useEffect(() => {
    const total = (isEditing ? editImages : moodBoard?.images || []).length;
    if (total > 0 && previewImageIndex >= total) {
      setPreviewImageIndex(0);
    }
  }, [editImages, moodBoard?.images, isEditing, previewImageIndex]);

  React.useLayoutEffect(() => {
    const isNewBoard = boardId === 'new';
    const showSaveButton = isNewBoard || isEditing;
    const isOwner =
      moodBoard && moodBoard.user_id === parseInt(user?.id || '0');
    const showEditButton = isOwner && !isEditing && !isNewBoard;
    const isViewingPost = boardId !== 'new' && !isEditing;

    // Update route params when entering edit mode so the header can respond
    if (isEditing && route.params?.isEditing !== true) {
      navigation.setParams({ isEditing: true } as any);
    }

    navigation.setOptions({
      title: 'Mood Board',
      headerRight: () => (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {showEditButton && (
            <TouchableOpacity
              style={{ padding: 8 }}
              onPress={() => setIsEditing(true)}
            >
              <Ionicons name="create-outline" size={24} color={colors.text} />
            </TouchableOpacity>
          )}

          {isViewingPost && (
            <TouchableOpacity
              style={{ padding: 8 }}
              onPress={() => setShowShareModal(true)}
            >
              <Ionicons name="share-outline" size={24} color={colors.text} />
            </TouchableOpacity>
          )}

          {showSaveButton && (
            <Button
              title={boardId === 'new' ? 'Post' : 'Save'}
              onPress={() => {
                trackButtonClick('Post', 'create-moodboard-screen', {
                  button_name: 'Post Moodboard',
                  button_type: 'post_moodboard',
                  screen: 'create-moodboard',
                  board_id: boardId,
                  has_title: !!editName.trim(),
                  images_count: editImages.length,
                  tags_count: editTags.length,
                  is_public: editIsPublic,
                });

                if (boardId === 'new') {
                  handleCreate();
                } else {
                  handleSaveChanges();
                }
              }}
              loading={saving}
              disabled={
                saving ||
                !editName.trim() ||
                editImages.length === 0 ||
                editTags.length < 1
              }
              style={{
                paddingHorizontal: 16,
                paddingVertical: 4,
                borderRadius: 8,
                marginLeft: 8,
                height: 32,
                minHeight: 32,
                maxHeight: 32,
              }}
              textStyle={{ fontSize: 14, fontWeight: '600' }}
              icon={
                <Ionicons
                  name={
                    boardId === 'new'
                      ? 'add-circle-outline'
                      : 'checkmark-circle-outline'
                  }
                  size={18}
                  color={
                    saving ||
                    !editName.trim() ||
                    editImages.length === 0 ||
                    editTags.length < 1
                      ? colors.secondary
                      : colors.background
                  }
                />
              }
              iconPosition="left"
            />
          )}
        </View>
      ),
    });
  }, [
    navigation,
    boardId,
    isEditing,
    moodBoard,
    user,
    colors,
    saving,
    editName,
    editImages,
    editTags,
    route.params,
  ]);

  // Ensure isEditing state stays in sync with route params
  useEffect(() => {
    if (initialIsEditing === true && !isEditing) {
      setIsEditing(true);
    }
  }, [initialIsEditing, isEditing]);

  useEffect(() => {
    if (boardId === 'new') {
      // Initialize new board
      setMoodBoard({
        id: 0,
        name: '',
        description: '',
        colors: [],
        images: [],
        tags: [],
        is_public: false,
        likes_count: 0,
        shares_count: 0,
        saves_count: 0,
        views_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: parseInt(user?.id || '0'),
        user_name: user?.name || user?.email.split('@')[0],
        user_profile_image: user?.profileImageUrl,
        is_liked: false,
        is_saved: false,
      });

      // Initialize editing state for new board
      setEditName('');
      setEditDescription('');
      setEditLocation('');
      setEditIsPublic(false);
      setEditTags([]);
      setEditColors([]);
      setEditImages([]);

      setLoading(false);
    } else {
      loadMoodBoard();
    }
  }, [boardId]);

  const loadMoodBoard = async () => {
    try {
      setLoading(true);
      setError(null);
      const board = await moodBoardAPI.getMoodBoard(parseInt(boardId));
      setMoodBoard(board);

      // Initialize editing state
      setEditName(board.name);
      setEditDescription(board.description || '');
      setEditLocation(board.location || '');
      setEditIsPublic(board.is_public);
      setEditTags([...board.tags]);
      setEditColors([...board.colors]);
      setEditImages([...board.images]);
    } catch {
      setError('Failed to load mood board');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    trackAnalyticsEvent({
      event: 'moodboard_create_attempt',
      screen: 'create-moodboard',
      has_title: !!editName.trim(),
      images_count: editImages.length,
      tags_count: editTags.length,
      is_public: editIsPublic,
    });

    if (!user) {
      Alert.alert('Error', 'You must be logged in to save mood boards');
      return;
    }

    if (!editName.trim()) {
      Alert.alert('Error', 'Please enter a name for your mood board');
      return;
    }

    const name = editName.trim();
    if (name.length > 15) {
      Alert.alert('Error', 'Title must be 15 characters or less');
      return;
    }

    try {
      setSaving(true);

      if (editImages.length === 0) {
        Alert.alert('Error', 'Please add at least one image');
        setSaving(false);
        return;
      }

      let base64Images: string[];
      try {
        base64Images = await Promise.all(editImages.map(uriToBase64));
      } catch {
        Alert.alert('Error', 'Failed to process images. Please try again.');
        setSaving(false);
        return;
      }
      const token = await getToken('auth_token');
      // Validate description length - backend limit is 50, not 100
      const description = editDescription.trim();
      if (description && description.length > 50) {
        Alert.alert('Error', 'Description must be 50 characters or less');
        setSaving(false);
        return;
      }
      if (editTags.length < 1) {
        Alert.alert('Error', 'Please add at least 1 tag');
        setSaving(false);
        return;
      }
      if (editTags.length > 10) {
        Alert.alert('Error', 'Maximum 10 tags allowed');
        setSaving(false);
        return;
      }
      const createData = {
        name: name.substring(0, 15),
        description: description || undefined,
        location: editLocation.trim() || undefined,
        colors: editColors,
        tags: editTags.slice(0, 10),
        is_public: editIsPublic,
        images: base64Images,
      };
      const response = await axios.post(`${API_URL}/mood-boards/`, createData, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          'Content-Type': 'application/json',
        },
      });
      const newBoard = response.data;

      trackAnalyticsEvent({
        event: 'moodboard_created',
        screen: 'create-moodboard',
        moodboard_id: newBoard.id,
        title: newBoard.name,
        images_count: newBoard.images?.length || 0,
        tags_count: newBoard.tags?.length || 0,
        is_public: newBoard.is_public,
      });

      setMoodBoard(newBoard);
      setIsEditing(false);

      // Clear only heavy state (base64 images, extracted colors) to free memory; keep moodBoard
      // so the success view stays visible until we navigate (no flicker).
      setEditImages([]);
      setExtractedColors({});
      const performNavigate = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const path = '/dashboard/mood-boards';
          if (window.location.pathname !== path)
            window.history.replaceState({}, '', path);
        }
        if (Platform.OS === 'web') {
          navigation.replace('MainTabs', {
            section: 'mood-boards',
            selectedTab: 'my',
          });
        } else {
          navigation.replace('MainTabs', {
            section: 'mood-boards',
            initialTab: 'MoodBoardTab',
            selectedTab: 'my',
          });
        }
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => setTimeout(performNavigate, 0));
      } else {
        setTimeout(performNavigate, 0);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to create moodboard. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!user) {
      Alert.alert('Error', 'You must be logged in to save mood boards');
      return;
    }

    if (!editName.trim()) {
      Alert.alert('Error', 'Please enter a name for your mood board');
      return;
    }

    // Validate name length
    const name = editName.trim();
    if (name.length > 15) {
      Alert.alert('Error', 'Title must be 15 characters or less');
      return;
    }

    try {
      setSaving(true);

      // Validate images exist
      if (editImages.length === 0) {
        Alert.alert('Error', 'Please add at least one image');
        setSaving(false);
        return;
      }

      // Validate description length - backend limit is 50, not 100
      const description = editDescription.trim();
      if (description && description.length > 50) {
        Alert.alert('Error', 'Description must be 50 characters or less');
        setSaving(false);
        return;
      }
      // Validate tags count - at least 1 tag is required
      if (editTags.length < 1) {
        Alert.alert('Error', 'Please add at least 1 tag');
        setSaving(false);
        return;
      }
      if (editTags.length > 10) {
        Alert.alert('Error', 'Maximum 10 tags allowed');
        setSaving(false);
        return;
      }
      // Update existing mood board
      const updateData: UpdateMoodBoardRequest = {
        name: name.substring(0, 15), // Enforce 15 char limit
        description: description || undefined,
        location: editLocation.trim() || undefined,
        colors: editColors,
        images: editImages,
        tags: editTags.slice(0, 10), // Enforce 10 tag limit
        is_public: editIsPublic,
      };

      const updatedBoard = await moodBoardAPI.update(
        parseInt(boardId),
        updateData
      );
      setMoodBoard(updatedBoard);
      setIsEditing(false);

      setEditImages([]);
      setExtractedColors({});
      const performNavigate = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const path = '/dashboard/mood-boards';
          if (window.location.pathname !== path)
            window.history.replaceState({}, '', path);
        }
        if (Platform.OS === 'web') {
          navigation.replace('MainTabs', {
            section: 'mood-boards',
            selectedTab: 'my',
          });
        } else {
          navigation.replace('MainTabs', {
            initialTab: 'MoodBoardTab',
            selectedTab: 'my',
          });
        }
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => setTimeout(performNavigate, 0));
      } else {
        setTimeout(performNavigate, 0);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to update mood board. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (boardId === 'new') {
      await handleCreate();
    } else {
      await handleSaveChanges();
    }
  };

  const handleDelete = async () => {
    if (!moodBoard || boardId === 'new') return;

    Alert.alert(
      'Delete Mood Board',
      'Are you sure you want to delete this mood board? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await moodBoardAPI.delete(moodBoard.id);
              isNavigatingAwayRef.current = true;
              setEditImages([]);
              setExtractedColors({});
              setMoodBoard(null);
              const performNavigate = () => {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  const path = '/dashboard/mood-boards';
                  if (window.location.pathname !== path)
                    window.history.replaceState({}, '', path);
                }
                navigation.replace('MainTabs', {
                  section: 'mood-boards',
                  selectedTab: 'my',
                });
              };
              if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => setTimeout(performNavigate, 0));
              } else {
                setTimeout(performNavigate, 0);
              }
            } catch {
              Alert.alert('Error', 'Failed to delete mood board');
            }
          },
        },
      ]
    );
  };

  const handleLike = async () => {
    if (!user || !moodBoard) {
      Alert.alert('Login Required', 'Please log in to like mood boards');
      return;
    }

    try {
      const result = await moodBoardAPI.toggleLike(moodBoard.id);
      // Use the authoritative count from backend
      setMoodBoard((prev) =>
        prev
          ? {
              ...prev,
              likes_count: result.likes_count, // Backend's authoritative count
              is_liked: result.liked,
            }
          : prev
      );
    } catch {
      Alert.alert('Error', 'Failed to update like status');
    }
  };

  const handleAddColor = (color: string) => {
    if (!editColors.includes(color)) {
      setEditColors([...editColors, color]);
    }
    setShowColorPicker(false);
  };

  const handleToggleColor = (color: string) => {
    if (editColors.includes(color)) {
      setEditColors(editColors.filter((c) => c !== color));
    } else {
      setEditColors([...editColors, color]);
    }
  };

  const handleRemoveColor = (index: number) => {
    setEditColors(editColors.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !editTags.includes(tag)) {
      if (editTags.length >= 10) {
        Alert.alert('Tag Limit', 'Maximum 10 tags allowed');
        return;
      }
      setEditTags([...editTags, tag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setEditTags(editTags.filter((_, i) => i !== index));
  };

  // Extract colors from an image using improved canvas analysis (web only)
  const extractColorsFromImage = async (
    imageUri: string
  ): Promise<string[]> => {
    try {
      // Only run on web platform
      if (Platform.OS !== 'web' || typeof window === 'undefined') {
        // Fallback for mobile - return some default colors
        return ['#F5F5F5', '#6B7280', '#374151', '#D97706'];
      }

      return new Promise((resolve) => {
        const img = new (window as any).Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            resolve([]);
            return;
          }

          // Set canvas size to analyze the image
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw image to canvas
          ctx.drawImage(img, 0, 0);

          // Get image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // Improved color extraction with better filtering
          const colorMap = new Map<string, number>();

          // Sample pixels more densely
          const step = Math.max(1, Math.floor(data.length / 4 / 10000)); // Sample every 10000th pixel

          for (let i = 0; i < data.length; i += step * 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            // Skip transparent pixels
            if (a < 128) continue;

            // Skip very dark colors (likely shadows or noise)
            if (r < 20 && g < 20 && b < 20) continue;

            // Skip very light colors (likely highlights or noise)
            if (r > 240 && g > 240 && b > 240) continue;

            // Skip grays (likely noise)
            const maxDiff = Math.max(
              Math.abs(r - g),
              Math.abs(r - b),
              Math.abs(g - b)
            );
            if (maxDiff < 30) continue; // Skip if RGB values are too similar (gray)

            // More aggressive quantization to group similar colors
            const quantizedR = Math.round(r / 64) * 64;
            const quantizedG = Math.round(g / 64) * 64;
            const quantizedB = Math.round(b / 64) * 64;

            // Convert to hex
            const hex = `#${(
              (1 << 24) +
              (quantizedR << 16) +
              (quantizedG << 8) +
              quantizedB
            )
              .toString(16)
              .slice(1)}`;

            // Count color frequency
            colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
          }

          // Sort colors by frequency and get top colors
          const sortedColors = Array.from(colorMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map((entry) => entry[0]);

          // Get diverse colors with strict filtering
          const diverseColors: string[] = [];
          for (const color of sortedColors) {
            if (diverseColors.length >= 5) break; // Get 5 candidates

            // Check if this color is significantly different from already selected colors
            const isDifferent = diverseColors.every((existingColor) => {
              const distance = getColorDistance(color, existingColor);
              return distance > 100; // Higher distance threshold
            });

            if (isDifferent) {
              diverseColors.push(color);
            }
          }

          // Take the top 4 most frequent diverse colors
          const finalColors = diverseColors.slice(0, 4);
          resolve(finalColors);
        };

        img.onerror = () => {
          resolve([]);
        };

        img.src = imageUri;
      });
    } catch {
      return [];
    }
  };

  // Calculate color distance using RGB values
  const getColorDistance = (color1: string, color2: string): number => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);

    if (!rgb1 || !rgb2) return 0;

    const dr = rgb1.r - rgb2.r;
    const dg = rgb1.g - rgb2.g;
    const db = rgb1.b - rgb2.b;

    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const hexToRgb = (
    hex: string
  ): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const handleImageUploaded = async (imageUri: string) => {
    const colors = await extractColorsFromImage(imageUri);
    setExtractedColors((prev) => ({
      ...prev,
      [imageUri]: colors,
    }));
  };

  const extractColorsFromExistingImages = async () => {
    if (isEditing && moodBoard && moodBoard.images.length > 0) {
      const newExtractedColors: { [imageUri: string]: string[] } = {};

      for (const imageUri of moodBoard.images) {
        if (!extractedColors[imageUri]) {
          const colors = await extractColorsFromImage(imageUri);
          newExtractedColors[imageUri] = colors;
        }
      }

      if (Object.keys(newExtractedColors).length > 0) {
        setExtractedColors((prev) => ({
          ...prev,
          ...newExtractedColors,
        }));
      }
    }
  };

  const handleToggleColorFromPhoto = (color: string) => {
    setEditColors((prevColors) => {
      const isSelected = prevColors.includes(color);

      if (isSelected) {
        // Remove color if already selected
        return prevColors.filter((c) => c !== color);
      } else {
        // Add color if not selected
        return [...prevColors, color];
      }
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
    return `${Math.floor(diffInSeconds / 604800)}w`;
  };

  // Generate preview data for the color story
  const getPreviewData = () => {
    const currentName = isEditing ? editName : moodBoard?.name || '';
    const currentDescription = isEditing
      ? editDescription
      : moodBoard?.description || '';
    const currentLocation = isEditing
      ? editLocation
      : moodBoard?.location || '';
    const currentColors = isEditing ? editColors : moodBoard?.colors || [];
    const currentImages = isEditing ? editImages : moodBoard?.images || [];
    const currentTags = isEditing ? editTags : moodBoard?.tags || [];

    // Generate color story based on colors
    const generateColorStory = (colors: string[]) => {
      if (colors.length === 0)
        return 'A harmonious blend of warm and cool tones';
      const stories = [
        'A harmonious blend of warm and cool tones',
        'Bold contrasts that demand attention',
        'Soft, dreamy pastels for gentle moods',
        'Rich, deep hues for sophisticated elegance',
        'Vibrant energy captured in every shade',
        'Minimalist beauty in monochromatic harmony',
        'Nature-inspired earth tones',
        'Modern pop with contemporary flair',
      ];
      return stories[Math.floor(Math.random() * stories.length)];
    };

    return {
      id: boardId === 'new' ? 'preview' : boardId,
      name: currentName || 'Untitled Color Story',
      description: currentDescription,
      location: currentLocation,
      colors: currentColors,
      images: currentImages,
      tags: currentTags,
      is_public: isEditing ? editIsPublic : moodBoard?.is_public || false,
      user_name: user?.name || user?.email?.split('@')[0] || 'Color Explorer',
      user_profile_image: user?.profileImageUrl,
      likes_count: 0,
      created_at: new Date().toISOString(),
      is_liked: false,
    };
  };

  // Convert preview data to MoodBoardCardItem format
  const getPreviewCardItem = (): MoodBoardCardItem => {
    const previewData = getPreviewData();
    const currentImages = isEditing ? editImages : moodBoard?.images || [];

    return {
      id: previewData.id.toString(),
      username: previewData.user_name || 'Color Explorer',
      userAvatar: previewData.user_profile_image,
      userEmail: user?.email,
      userId: parseInt(user?.id || '0'),
      title: previewData.name,
      description: previewData.description || null,
      location: previewData.location || null,
      image: currentImages.length > 0 ? currentImages[0] : null,
      images: currentImages,
      colors: previewData.colors,
      likes: previewData.likes_count,
      tags: previewData.tags,
      timestamp: formatTimeAgo(previewData.created_at),
      shares: 0,
      saved: 0,
      isTrending: false,
      is_public: previewData.is_public,
      isLiked: previewData.is_liked,
      isSaved: false,
    };
  };

  // Preview card actions
  const previewActions: MoodBoardCardActions = {
    onPress: () => {},
    onLike: () => {},
    onShare: () => {},
    onSave: () => {},
  };

  const previewImageNavigation: ImageNavigationProps = {
    currentIndex: previewImageIndex,
    totalImages: (isEditing ? editImages : moodBoard?.images || []).length,
    onNext: () => {
      const total = (isEditing ? editImages : moodBoard?.images || []).length;
      if (total > 0) {
        setPreviewImageIndex((prev) => (prev + 1) % total);
      }
    },
    onPrev: () => {
      const total = (isEditing ? editImages : moodBoard?.images || []).length;
      if (total > 0) {
        setPreviewImageIndex((prev) => (prev - 1 + total) % total);
      }
    },
    showCount: true,
  };

  const renderPreviewCard = () => {
    const previewItem = getPreviewCardItem();
    const screenWidth = Dimensions.get('window').width;
    const isMobile = Platform.OS !== 'web' || screenWidth < 768;

    // On mobile, always use vertical layout
    const layout = isMobile ? 'horizontal' : previewLayout;

    if (layout === 'thumbnail') {
      return (
        <ThumbnailCard
          item={previewItem}
          actions={previewActions}
          imageNavigation={previewImageNavigation}
        />
      );
    } else {
      return (
        <VerticalMoodboardCard
          item={previewItem}
          actions={previewActions}
          imageNavigation={previewImageNavigation}
          showEditButton={false}
          currentUserId={user?.id}
        />
      );
    }
  };

  if (loading || (isNavigatingAwayRef.current && !moodBoard)) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.loadingContainer}>
          <Text style={[styles.loadingText, { color: colors.secondary }]}>
            {isNavigatingAwayRef.current
              ? 'Taking you back...'
              : 'Loading mood board...'}
          </Text>
        </View>
      </View>
    );
  }

  if (error || !moodBoard) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={48} color="#EF4444" />
          <Text style={styles.errorText}>
            {error || 'Mood board not found'}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadMoodBoard}>
            <Text style={styles.retryText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Determine if current user owns this post
  const isOwner = moodBoard && moodBoard.user_id === parseInt(user?.id || '0');
  const isViewingPost = boardId !== 'new' && !isEditing;

  return (
    <LinearGradient
      colors={[
        hexToRgba(colors.appGradient1, 0.8),
        hexToRgba(colors.appGradient2, 0.8),
      ]}
      style={styles.container}
      start={{ x: 0.5, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
    >
      <View style={styles.responsiveContainer}>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Show Preview Card for All Posts When Viewing */}
          {isViewingPost ? (
            <View style={styles.previewContainer}>
              <View style={styles.previewCardWrapper}>
                {renderPreviewCard()}
              </View>
            </View>
          ) : (
            <>
              {/* Header Section - Only for Own Posts */}
              <View
                style={[
                  styles.headerSection,
                  {
                    backgroundColor: colors.card,
                    borderBottomColor: colors.border,
                  },
                ]}
              >
                <View style={styles.headerContent}>
                  <View style={styles.headerInfo}>
                    {!isEditing && (
                      <View style={styles.headerMeta}>
                        <Text
                          style={[
                            styles.headerSubtitle,
                            { color: colors.secondary },
                          ]}
                        >
                          Created {formatDate(moodBoard.created_at)}
                        </Text>
                        {/* Privacy status in header */}
                        <View
                          style={[
                            styles.headerPrivacyBadge,
                            {
                              backgroundColor: moodBoard.is_public
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(107, 114, 128, 0.1)',
                            },
                          ]}
                        >
                          <Ionicons
                            name={
                              moodBoard.is_public
                                ? 'globe-outline'
                                : 'lock-closed'
                            }
                            size={14}
                            color={
                              moodBoard.is_public
                                ? palette.brightGreen
                                : palette.darkGrayBlue
                            }
                          />
                          <Text
                            style={[
                              styles.headerPrivacyText,
                              {
                                color: moodBoard.is_public
                                  ? palette.brightGreen
                                  : palette.darkGrayBlue,
                              },
                            ]}
                          >
                            {moodBoard.is_public ? 'Public' : 'Private'}
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>
                  {!isEditing && isOwner && (
                    <View style={styles.headerActions}>
                      <TouchableOpacity
                        style={[
                          styles.actionButton,
                          { backgroundColor: colors.primary + '15' },
                        ]}
                        onPress={() => setIsEditing(true)}
                      >
                        <Ionicons
                          name="create-outline"
                          size={20}
                          color={colors.primary}
                        />
                      </TouchableOpacity>
                      {/* Share button temporarily hidden
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: colors.primary + '15' }]}
                onPress={() => setShowShareModal(true)}
              >
                <Ionicons name="share-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
              */}
                    </View>
                  )}
                  {/* Share button temporarily hidden for non-owners too
          {!isEditing && !isOwner && (
            <View style={styles.headerActions}>
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: colors.primary + '15' }]}
                onPress={() => setShowShareModal(true)}
              >
                <Ionicons name="share-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
          )}
          */}
                </View>
              </View>

              {/* Main Content */}
              <View
                style={[
                  styles.mainContent,
                  { backgroundColor: colors.background },
                ]}
              >
                {/* Side by Side Layout for Editing and Creating */}

                {isEditing || boardId === 'new' ? (
                  <View style={styles.sideBySideContainer}>
                    {/* Left Side - Creation Form */}
                    <View style={styles.leftColumn}>
                      <View style={styles.panelHeader}>
                        <View style={styles.panelHeaderLeft}>
                          <View
                            style={[
                              styles.panelIconContainer,
                              { backgroundColor: `${colors.primary}15` },
                            ]}
                          >
                            <Ionicons
                              name="create"
                              size={Platform.OS === 'web' ? 22 : 24}
                              color={colors.primary}
                            />
                          </View>
                          <Text
                            style={[styles.panelTitle, { color: colors.text }]}
                          >
                            {boardId === 'new'
                              ? 'Create Moodboard'
                              : 'Edit Moodboard'}
                          </Text>
                        </View>
                        {/* Post Button on same level as title */}
                        <Button
                          title={boardId === 'new' ? 'Post' : 'Update'}
                          onPress={() => {
                            trackButtonClick(
                              'Post',
                              'create-moodboard-screen',
                              {
                                button_name: 'Post Moodboard',
                                button_type: 'post_moodboard',
                                screen: 'create-moodboard',
                                location: 'header',
                                board_id: boardId,
                                has_title: !!editName.trim(),
                                images_count: editImages.length,
                                tags_count: editTags.length,
                                is_public: editIsPublic,
                              }
                            );

                            if (boardId === 'new') {
                              handleCreate();
                            } else {
                              handleSaveChanges();
                            }
                          }}
                          loading={saving}
                          variant="primary"
                          disabled={
                            saving ||
                            !editName.trim() ||
                            editImages.length === 0 ||
                            editTags.length < 1
                          }
                          style={styles.headerPostButton}
                          textStyle={styles.headerPostText}
                          icon={
                            <Ionicons
                              name="paper-plane"
                              size={Platform.OS === 'web' ? 14 : 18}
                              color={palette.white}
                            />
                          }
                        />
                      </View>

                      <ScrollView
                        style={styles.leftScrollContent}
                        showsVerticalScrollIndicator={false}
                      >
                        {/* Basic Info Section */}
                        <View
                          style={[
                            styles.contentCard,
                            { backgroundColor: colors.card },
                          ]}
                        >
                          <View style={styles.cardHeader}>
                            <View style={styles.cardHeaderLeft}>
                              <Ionicons
                                name="information-circle-outline"
                                size={24}
                                color={colors.primary}
                              />
                              <Text
                                style={[
                                  styles.cardTitle,
                                  { color: colors.text },
                                ]}
                              >
                                Moodboard Details
                              </Text>
                            </View>
                            <View style={styles.cardHeaderBadge}>
                              <Text
                                style={[
                                  styles.cardHeaderBadgeText,
                                  { color: colors.primary },
                                ]}
                              >
                                Required
                              </Text>
                            </View>
                          </View>

                          {/* Name */}
                          <View
                            style={[styles.inputGroup, styles.titleSection]}
                          >
                            <View style={styles.inputLabelContainer}>
                              <Text
                                style={[
                                  styles.inputLabel,
                                  { color: colors.text },
                                ]}
                              >
                                Title
                              </Text>
                              <Text
                                style={[
                                  styles.inputRequired,
                                  { color: palette.brightMatteRed },
                                ]}
                              >
                                *
                              </Text>
                            </View>
                            {isEditing ? (
                              <View>
                                <TextField
                                  value={editName}
                                  onChangeText={(text) => {
                                    if (text.length <= 15) {
                                      setEditName(text);
                                    }
                                  }}
                                  placeholder="Enter your moodboard title"
                                  maxLength={15}
                                  showCharCount={true}
                                />
                              </View>
                            ) : (
                              <Text
                                style={[
                                  styles.displayText,
                                  { color: colors.text },
                                ]}
                              >
                                {moodBoard.name}
                              </Text>
                            )}
                          </View>

                          {/* Description */}
                          <View style={styles.inputGroup}>
                            <View style={styles.inputLabelContainer}>
                              <Text
                                style={[
                                  styles.inputLabel,
                                  { color: colors.text },
                                ]}
                              >
                                Description
                              </Text>
                              <Text
                                style={[
                                  styles.inputOptional,
                                  { color: colors.secondary },
                                ]}
                              >
                                Optional
                              </Text>
                            </View>
                            {isEditing ? (
                              <View>
                                <TextField
                                  value={editDescription}
                                  onChangeText={(text) => {
                                    if (text.length <= 50) {
                                      setEditDescription(text);
                                    }
                                  }}
                                  placeholder="Describe your color story (e.g., 'Summer vibes with warm tones')"
                                  multiline
                                  numberOfLines={3}
                                  maxLength={50}
                                  showCharCount={true}
                                />
                              </View>
                            ) : (
                              <Text
                                style={[
                                  styles.displayText,
                                  { color: colors.secondary },
                                ]}
                              >
                                {moodBoard.description ||
                                  'No description provided'}
                              </Text>
                            )}
                          </View>

                          {/* Location */}
                          <View style={styles.inputGroup}>
                            <View style={styles.inputLabelContainer}>
                              <Text
                                style={[
                                  styles.inputLabel,
                                  { color: colors.text },
                                ]}
                              >
                                Location
                              </Text>
                              <Text
                                style={[
                                  styles.inputOptional,
                                  { color: colors.secondary },
                                ]}
                              >
                                Optional
                              </Text>
                            </View>
                            {isEditing ? (
                              <LocationPicker
                                value={editLocation}
                                onChange={setEditLocation}
                                placeholder="Search for a location or enter manually"
                              />
                            ) : (
                              <>
                                {moodBoard.location ? (
                                  <>
                                    <Text
                                      style={[
                                        styles.displayText,
                                        {
                                          color: colors.secondary,
                                          marginBottom: 8,
                                        },
                                      ]}
                                    >
                                      {moodBoard.location}
                                    </Text>
                                    <LocationMap
                                      location={moodBoard.location}
                                      height={200}
                                      showControls={true}
                                    />
                                  </>
                                ) : (
                                  <Text
                                    style={[
                                      styles.displayText,
                                      { color: colors.secondary },
                                    ]}
                                  >
                                    No location provided
                                  </Text>
                                )}
                              </>
                            )}
                          </View>

                          {/* Privacy Settings */}
                          {isEditing && (
                            <View
                              style={[styles.inputGroup, styles.privacySection]}
                            >
                              <View style={styles.privacyToggle}>
                                <View style={styles.privacyInfo}>
                                  <View style={styles.inputLabelContainer}>
                                    <Text
                                      style={[
                                        styles.inputLabel,
                                        { color: colors.text },
                                      ]}
                                    >
                                      Make Public
                                    </Text>
                                    <Text
                                      style={[
                                        styles.inputOptional,
                                        { color: colors.secondary },
                                      ]}
                                    >
                                      Optional
                                    </Text>
                                  </View>
                                  <Text
                                    style={[
                                      styles.privacyDescription,
                                      { color: colors.secondary },
                                    ]}
                                  >
                                    Allow others to discover and like your story
                                  </Text>
                                </View>
                                <TouchableOpacity
                                  style={[
                                    styles.modernToggle,
                                    {
                                      backgroundColor: editIsPublic
                                        ? colors.primary
                                        : colors.border,
                                    },
                                  ]}
                                  onPress={() => setEditIsPublic(!editIsPublic)}
                                >
                                  <View
                                    style={[
                                      styles.toggleKnob,
                                      {
                                        backgroundColor: palette.white,
                                        transform: [
                                          { translateX: editIsPublic ? 20 : 2 },
                                        ],
                                      },
                                    ]}
                                  />
                                </TouchableOpacity>
                              </View>
                            </View>
                          )}
                        </View>

                        {/* Images Section */}
                        <View
                          style={[
                            styles.contentCard,
                            { backgroundColor: colors.card },
                          ]}
                        >
                          <View style={styles.cardHeader}>
                            <View style={styles.cardHeaderLeft}>
                              <Ionicons
                                name="images-outline"
                                size={24}
                                color={colors.primary}
                              />
                              <Text
                                style={[
                                  styles.cardTitle,
                                  { color: colors.text },
                                ]}
                              >
                                Images
                              </Text>
                            </View>
                            <View style={styles.cardHeaderBadge}>
                              <Text
                                style={[
                                  styles.cardHeaderBadgeText,
                                  { color: colors.primary },
                                ]}
                              >
                                Required
                              </Text>
                            </View>
                          </View>

                          {isEditing ? (
                            <ImageUploader
                              onImageSelected={async (imageUri) => {
                                setEditImages([...editImages, imageUri]);
                                await handleImageUploaded(imageUri);
                              }}
                              existingImages={editImages}
                              onRemoveImage={(index) => {
                                const removedImage = editImages[index];
                                setEditImages(
                                  editImages.filter((_, i) => i !== index)
                                );
                                // Remove extracted colors for this image
                                setExtractedColors((prev) => {
                                  const newColors = { ...prev };
                                  delete newColors[removedImage];
                                  return newColors;
                                });
                              }}
                              maxImages={4}
                              required={true}
                            />
                          ) : moodBoard.images.length > 0 ? (
                            <View style={styles.modernImagesGrid}>
                              {moodBoard.images.map((imageUri, index) => (
                                <View
                                  key={index}
                                  style={styles.modernImageItem}
                                >
                                  <Image
                                    source={{ uri: imageUri }}
                                    style={styles.modernImagePreview}
                                  />
                                </View>
                              ))}
                            </View>
                          ) : (
                            <View style={styles.emptyState}>
                              <Ionicons
                                name="images-outline"
                                size={48}
                                color={colors.secondary}
                              />
                              <Text
                                style={[
                                  styles.emptyText,
                                  { color: colors.secondary },
                                ]}
                              >
                                No images added yet
                              </Text>
                            </View>
                          )}
                        </View>

                        {/* Photo Colors Section */}
                        <View
                          style={[
                            styles.contentCard,
                            { backgroundColor: colors.card },
                          ]}
                        >
                          <View style={styles.cardHeader}>
                            <View style={styles.cardHeaderLeft}>
                              <Ionicons
                                name="images-outline"
                                size={24}
                                color={colors.primary}
                              />
                              <Text
                                style={[
                                  styles.cardTitle,
                                  { color: colors.text },
                                ]}
                              >
                                Photo Colors
                              </Text>
                            </View>
                            <View style={styles.cardHeaderBadge}>
                              <Text
                                style={[
                                  styles.cardHeaderBadgeText,
                                  { color: colors.primary },
                                ]}
                              >
                                {editColors.length} selected
                              </Text>
                            </View>
                          </View>

                          {Object.keys(extractedColors).length > 0 ? (
                            <View style={styles.photoColorsContainer}>
                              {/* Show existing images first */}
                              {moodBoard &&
                                moodBoard.images.map((imageUri, imageIndex) => {
                                  const colors = extractedColors[imageUri];
                                  if (!colors || colors.length === 0)
                                    return null;

                                  return (
                                    <View
                                      key={`existing-${imageUri}`}
                                      style={styles.photoColorRow}
                                    >
                                      <View style={styles.photoThumbnail}>
                                        <Image
                                          source={{ uri: imageUri }}
                                          style={styles.photoThumbnailImage}
                                        />
                                      </View>
                                      <View style={styles.photoColorSwatches}>
                                        {colors.map((color, colorIndex) => (
                                          <TouchableOpacity
                                            key={colorIndex}
                                            style={[
                                              styles.photoColorSwatch,
                                              { backgroundColor: color },
                                              editColors.includes(color) &&
                                                styles.selectedPhotoColor,
                                            ]}
                                            onPress={() =>
                                              handleToggleColorFromPhoto(color)
                                            }
                                          >
                                            {editColors.includes(color) && (
                                              <Ionicons
                                                name="checkmark"
                                                size={12}
                                                color={palette.white}
                                              />
                                            )}
                                          </TouchableOpacity>
                                        ))}
                                      </View>
                                    </View>
                                  );
                                })}

                              {/* Show newly uploaded images */}
                              {editImages
                                .filter(
                                  (img) => !moodBoard?.images.includes(img)
                                )
                                .map((imageUri, imageIndex) => {
                                  const colors = extractedColors[imageUri];
                                  if (!colors || colors.length === 0)
                                    return null;

                                  return (
                                    <View
                                      key={`new-${imageUri}`}
                                      style={styles.photoColorRow}
                                    >
                                      <View style={styles.photoThumbnail}>
                                        <Image
                                          source={{ uri: imageUri }}
                                          style={styles.photoThumbnailImage}
                                        />
                                      </View>
                                      <View style={styles.photoColorSwatches}>
                                        {colors.map((color, colorIndex) => (
                                          <TouchableOpacity
                                            key={colorIndex}
                                            style={[
                                              styles.photoColorSwatch,
                                              { backgroundColor: color },
                                              editColors.includes(color) &&
                                                styles.selectedPhotoColor,
                                            ]}
                                            onPress={() =>
                                              handleToggleColorFromPhoto(color)
                                            }
                                          >
                                            {editColors.includes(color) && (
                                              <Ionicons
                                                name="checkmark"
                                                size={12}
                                                color={palette.white}
                                              />
                                            )}
                                          </TouchableOpacity>
                                        ))}
                                      </View>
                                    </View>
                                  );
                                })}
                            </View>
                          ) : (
                            <View style={styles.emptyState}>
                              <Ionicons
                                name="images-outline"
                                size={48}
                                color={colors.secondary}
                              />
                              <Text
                                style={[
                                  styles.emptyText,
                                  { color: colors.secondary },
                                ]}
                              >
                                Upload images to extract colors automatically
                              </Text>
                            </View>
                          )}
                        </View>

                        {/* Tags Section */}
                        <View
                          style={[
                            styles.contentCard,
                            { backgroundColor: colors.card },
                          ]}
                        >
                          <View style={styles.cardHeader}>
                            <View style={styles.cardHeaderLeft}>
                              <Ionicons
                                name="pricetag-outline"
                                size={24}
                                color={colors.primary}
                              />
                              <Text
                                style={[
                                  styles.cardTitle,
                                  { color: colors.text },
                                ]}
                              >
                                Tags
                              </Text>
                            </View>
                          </View>

                          {isEditing && (
                            <View>
                              <View style={styles.simpleTagInput}>
                                <TextField
                                  value={newTag}
                                  onChangeText={setNewTag}
                                  onFocus={() => setTagFieldTouched(true)}
                                  placeholder={
                                    editTags.length >= 10
                                      ? 'Maximum 10 tags reached'
                                      : editTags.length < 1
                                        ? 'e.g., #autumn, #warmtones, #casual (required)'
                                        : 'Add another tag and press Enter'
                                  }
                                  onSubmitEditing={handleAddTag}
                                  editable={editTags.length < 10}
                                  error={
                                    tagFieldTouched && editTags.length < 1
                                      ? 'At least 1 tag is required'
                                      : undefined
                                  }
                                  helperText={
                                    editTags.length >= 1 && editTags.length < 10
                                      ? `${editTags.length}/10 tags`
                                      : undefined
                                  }
                                />
                              </View>
                              {editTags.length >= 10 && (
                                <Text
                                  style={[
                                    styles.tagWarning,
                                    { color: palette.brightMatteRed },
                                  ]}
                                >
                                  Maximum 10 tags reached
                                </Text>
                              )}
                            </View>
                          )}

                          {(isEditing ? editTags : moodBoard.tags).length >
                          0 ? (
                            <View style={styles.simpleTagsContainer}>
                              {(isEditing ? editTags : moodBoard.tags).map(
                                (tag, index) => (
                                  <View
                                    key={index}
                                    style={[
                                      styles.simpleTag,
                                      {
                                        backgroundColor: colors.primary + '15',
                                      },
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.simpleTagText,
                                        { color: colors.primary },
                                      ]}
                                    >
                                      #{tag}
                                    </Text>
                                    {isEditing && (
                                      <TouchableOpacity
                                        style={styles.simpleRemoveTagButton}
                                        onPress={() => handleRemoveTag(index)}
                                      >
                                        <Ionicons
                                          name="close"
                                          size={12}
                                          color={colors.primary}
                                        />
                                      </TouchableOpacity>
                                    )}
                                  </View>
                                )
                              )}
                            </View>
                          ) : (
                            <Text
                              style={[
                                styles.simpleEmptyText,
                                { color: colors.secondary },
                              ]}
                            >
                              {isEditing
                                ? 'Add tags to help others discover your story'
                                : 'No tags added yet'}
                            </Text>
                          )}
                        </View>
                      </ScrollView>
                    </View>

                    {/* Right Side - Live Preview (Web only) */}
                    {Platform.OS === 'web' &&
                      Dimensions.get('window').width >= 768 && (
                        <View style={styles.rightColumn}>
                          <View style={styles.panelHeader}>
                            <View style={styles.panelHeaderLeft}>
                              <Ionicons
                                name="eye-outline"
                                size={20}
                                color={colors.primary}
                              />
                              <Text
                                style={[
                                  styles.panelTitle,
                                  { color: colors.text },
                                ]}
                              >
                                Live Preview
                              </Text>
                            </View>
                            <LayoutToggle
                              layout={previewLayout}
                              onLayoutChange={(layout) => {
                                setPreviewLayout(layout);
                                setPreviewImageIndex(0);
                              }}
                            />
                          </View>

                          <View style={styles.previewWrapper}>
                            {renderPreviewCard()}
                          </View>
                        </View>
                      )}
                  </View>
                ) : (
                  // Regular layout for non-editing mode
                  <>
                    {/* Basic Info Section */}
                    <View
                      style={[
                        styles.contentCard,
                        { backgroundColor: colors.card },
                      ]}
                    >
                      <View style={styles.cardHeader}>
                        <Ionicons
                          name="information-circle-outline"
                          size={24}
                          color={colors.primary}
                        />
                        <Text
                          style={[styles.cardTitle, { color: colors.text }]}
                        >
                          Moodboard Details
                        </Text>
                        {/* Privacy indicator */}
                        <View
                          style={[
                            styles.privacyIndicator,
                            {
                              backgroundColor: moodBoard.is_public
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(107, 114, 128, 0.1)',
                            },
                          ]}
                        >
                          <Ionicons
                            name={
                              moodBoard.is_public
                                ? 'globe-outline'
                                : 'lock-closed'
                            }
                            size={16}
                            color={
                              moodBoard.is_public
                                ? palette.brightGreen
                                : palette.darkGrayBlue
                            }
                          />
                          <Text
                            style={[
                              styles.privacyIndicatorText,
                              {
                                color: moodBoard.is_public
                                  ? palette.brightGreen
                                  : palette.darkGrayBlue,
                              },
                            ]}
                          >
                            {moodBoard.is_public ? 'Public' : 'Private'}
                          </Text>
                        </View>
                      </View>

                      {/* Name */}
                      <View style={styles.inputGroup}>
                        <Text
                          style={[styles.inputLabel, { color: colors.text }]}
                        >
                          Name
                        </Text>
                        <Text
                          style={[styles.displayText, { color: colors.text }]}
                        >
                          {moodBoard.name}
                        </Text>
                      </View>

                      {/* Description */}
                      <View style={styles.inputGroup}>
                        <Text
                          style={[styles.inputLabel, { color: colors.text }]}
                        >
                          Description
                        </Text>
                        <Text
                          style={[
                            styles.displayText,
                            { color: colors.secondary },
                          ]}
                        >
                          {moodBoard.description || 'No description provided'}
                        </Text>
                      </View>

                      {/* Location */}
                      <View style={styles.inputGroup}>
                        <Text
                          style={[styles.inputLabel, { color: colors.text }]}
                        >
                          Location
                        </Text>
                        {moodBoard.location ? (
                          <>
                            <View style={styles.locationRow}>
                              <Ionicons
                                name="location-outline"
                                size={16}
                                color={colors.secondary}
                              />
                              <Text
                                style={[
                                  styles.displayText,
                                  { color: colors.secondary, marginLeft: 4 },
                                ]}
                              >
                                {moodBoard.location}
                              </Text>
                            </View>
                            <LocationMap
                              location={moodBoard.location}
                              height={200}
                              showControls={true}
                            />
                          </>
                        ) : (
                          <Text
                            style={[
                              styles.displayText,
                              { color: colors.secondary },
                            ]}
                          >
                            No location provided
                          </Text>
                        )}
                      </View>
                    </View>

                    {/* Images Section */}
                    <View
                      style={[
                        styles.contentCard,
                        { backgroundColor: colors.card },
                      ]}
                    >
                      <View style={styles.cardHeader}>
                        <Ionicons
                          name="images-outline"
                          size={24}
                          color={colors.primary}
                        />
                        <Text
                          style={[styles.cardTitle, { color: colors.text }]}
                        >
                          Images
                        </Text>
                      </View>

                      {moodBoard.images.length > 0 ? (
                        <View style={styles.modernImagesGrid}>
                          {moodBoard.images.map((imageUri, index) => (
                            <View key={index} style={styles.modernImageItem}>
                              <Image
                                source={{ uri: imageUri }}
                                style={styles.modernImagePreview}
                              />
                            </View>
                          ))}
                        </View>
                      ) : (
                        <View style={styles.emptyState}>
                          <Ionicons
                            name="images-outline"
                            size={48}
                            color={colors.secondary}
                          />
                          <Text
                            style={[
                              styles.emptyText,
                              { color: colors.secondary },
                            ]}
                          >
                            No images added yet
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Colors Section */}
                    <View
                      style={[
                        styles.contentCard,
                        { backgroundColor: colors.card },
                      ]}
                    >
                      <View style={styles.cardHeader}>
                        <Ionicons
                          name="color-palette-outline"
                          size={24}
                          color={colors.primary}
                        />
                        <Text
                          style={[styles.cardTitle, { color: colors.text }]}
                        >
                          Selected Colors
                        </Text>
                      </View>

                      {moodBoard.colors.length > 0 ? (
                        <View style={styles.modernColorsGrid}>
                          {moodBoard.colors.map((color, index) => (
                            <View key={index} style={styles.modernColorItem}>
                              <View
                                style={[
                                  styles.modernColorSwatch,
                                  { backgroundColor: color },
                                ]}
                              />
                              <Text
                                style={[
                                  styles.modernColorCode,
                                  { color: colors.text },
                                ]}
                              >
                                {color}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ) : (
                        <View style={styles.emptyState}>
                          <Ionicons
                            name="color-palette-outline"
                            size={48}
                            color={colors.secondary}
                          />
                          <Text
                            style={[
                              styles.emptyText,
                              { color: colors.secondary },
                            ]}
                          >
                            No colors added yet
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Tags Section */}
                    <View
                      style={[
                        styles.contentCard,
                        { backgroundColor: colors.card },
                      ]}
                    >
                      <View style={styles.cardHeader}>
                        <Ionicons
                          name="pricetag-outline"
                          size={24}
                          color={colors.primary}
                        />
                        <Text
                          style={[styles.cardTitle, { color: colors.text }]}
                        >
                          Tags
                        </Text>
                      </View>

                      <View style={styles.modernTagsContainer}>
                        {moodBoard.tags.map((tag, index) => (
                          <View
                            key={index}
                            style={[
                              styles.modernTag,
                              { backgroundColor: colors.primary + '15' },
                            ]}
                          >
                            <Text
                              style={[
                                styles.modernTagText,
                                { color: colors.primary },
                              ]}
                            >
                              #{tag}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>

                    {/* Stats and Actions */}
                    {!isEditing && boardId !== 'new' && (
                      <View
                        style={[
                          styles.contentCard,
                          { backgroundColor: colors.card },
                        ]}
                      >
                        <View style={styles.cardHeader}>
                          <Ionicons
                            name="stats-chart-outline"
                            size={24}
                            color={colors.primary}
                          />
                          <Text
                            style={[styles.cardTitle, { color: colors.text }]}
                          >
                            Stats & Actions
                          </Text>
                        </View>

                        <View style={styles.statsContainer}>
                          <View style={styles.stat}>
                            <Text
                              style={[styles.statValue, { color: colors.text }]}
                            >
                              {moodBoard.likes_count}
                            </Text>
                            <Text
                              style={[
                                styles.statLabel,
                                { color: colors.secondary },
                              ]}
                            >
                              Likes
                            </Text>
                          </View>
                          <View style={styles.stat}>
                            <Text
                              style={[styles.statValue, { color: colors.text }]}
                            >
                              {moodBoard.views_count}
                            </Text>
                            <Text
                              style={[
                                styles.statLabel,
                                { color: colors.secondary },
                              ]}
                            >
                              Views
                            </Text>
                          </View>
                        </View>

                        <View style={styles.actionsContainer}>
                          <TouchableOpacity
                            style={[
                              styles.actionButton,
                              {
                                backgroundColor: colors.primary + '15',
                                borderColor: colors.border,
                              },
                            ]}
                            onPress={handleLike}
                          >
                            <Ionicons
                              name={
                                moodBoard.is_liked ? 'heart' : 'heart-outline'
                              }
                              size={20}
                              color={
                                moodBoard.is_liked
                                  ? palette.brightMatteRed
                                  : colors.primary
                              }
                            />
                            <Text
                              style={[
                                styles.actionButtonText,
                                {
                                  color: moodBoard.is_liked
                                    ? palette.brightMatteRed
                                    : colors.primary,
                                },
                              ]}
                            >
                              {moodBoard.is_liked ? 'Liked' : 'Like'}
                            </Text>
                          </TouchableOpacity>

                          {moodBoard.user_id === parseInt(user?.id || '0') && (
                            <TouchableOpacity
                              style={[
                                styles.actionButton,
                                { backgroundColor: palette.brightMatteRed },
                              ]}
                              onPress={handleDelete}
                            >
                              <Ionicons
                                name="trash-outline"
                                size={20}
                                color={palette.white}
                              />
                              <Text
                                style={[
                                  styles.actionButtonText,
                                  { color: palette.white },
                                ]}
                              >
                                Delete
                              </Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    )}

                    {/* Metadata */}
                    {!isEditing && (
                      <View
                        style={[
                          styles.contentCard,
                          { backgroundColor: colors.card },
                        ]}
                      >
                        <View style={styles.cardHeader}>
                          <Ionicons
                            name="information-circle-outline"
                            size={24}
                            color={colors.primary}
                          />
                          <Text
                            style={[styles.cardTitle, { color: colors.text }]}
                          >
                            Details
                          </Text>
                        </View>

                        <Text
                          style={[styles.metaText, { color: colors.secondary }]}
                        >
                          Created on {formatDate(moodBoard.created_at)}
                        </Text>
                        {moodBoard.user_name && (
                          <Text
                            style={[
                              styles.metaText,
                              { color: colors.secondary },
                            ]}
                          >
                            by @{moodBoard.user_name}
                          </Text>
                        )}
                      </View>
                    )}
                  </>
                )}
              </View>
            </>
          )}
        </ScrollView>
      </View>

      {/* Color Generator Modal */}
      <Modal
        visible={showColorPicker}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: colors.background },
          ]}
        >
          <View style={styles.modalResponsiveWrapper}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowColorPicker(false)}>
                <Text
                  style={[styles.modalCancelText, { color: colors.primary }]}
                >
                  Cancel
                </Text>
              </TouchableOpacity>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Pick a Color
              </Text>
              <View style={{ width: 60 }} />
            </View>
            <ColorPicker
              selectedColors={editColors}
              onColorToggle={handleToggleColor}
            />
          </View>
        </View>
      </Modal>

      {/* Social Share Modal */}
      {moodBoard && (
        <SocialShareModal
          visible={showShareModal}
          onClose={() => setShowShareModal(false)}
          moodBoard={{
            id: moodBoard.id.toString(),
            title: moodBoard.name,
            username: moodBoard.user_name || 'Unknown',
            colors: moodBoard.colors,
            tags: moodBoard.tags,
            images: moodBoard.images,
          }}
          onShared={(updated) => {
            setMoodBoard((prev) =>
              prev ? { ...prev, shares_count: updated.shares_count } : prev
            );
          }}
        />
      )}
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  responsiveContainer: {
    flex: 1,
    width: '100%',
    minWidth: 320,
    maxWidth:
      Platform.OS === 'web'
        ? Math.min(Dimensions.get('window').width * 0.95, 1400)
        : '100%',
    alignSelf: 'center',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        maxWidth: Math.min(Dimensions.get('window').width * 0.9, 1400),
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        maxWidth: Math.min(Dimensions.get('window').width * 0.85, 1400),
      }),
  },
  previewContainer: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    width: '100%',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        padding: 20,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        padding: 24,
      }),
  },
  previewCardWrapper: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 600 : '100%',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        maxWidth: 700,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        maxWidth: '100%',
      }),
  },
  content: {
    flex: 1,
    padding: 0,
  },

  // Header Section
  headerSection: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        paddingHorizontal: 20,
        paddingVertical: 24,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        paddingHorizontal: 24,
        paddingVertical: 28,
      }),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        fontSize: 24,
      }),
  },
  headerSubtitle: {
    fontSize: 14,
    opacity: 0.7,
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  headerPrivacyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 12,
  },
  headerPrivacyText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Main Content
  mainContent: {
    padding: 12,
    gap: 8,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        padding: 16,
        gap: 12,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        padding: 20,
        gap: 16,
      }),
  },

  // Side by Side Layout
  sideBySideContainer: {
    flexDirection: 'column',
    gap: Platform.OS === 'web' ? 12 : 16,
    flex: 1,
    minWidth: 300,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 16,
    paddingTop: Platform.OS === 'web' ? 0 : 8,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        flexDirection: 'row',
        gap: 16,
        paddingHorizontal: 16,
        minWidth: 600,
        paddingTop: 0,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        gap: 20,
        paddingHorizontal: 20,
      }),
  },

  leftColumn: {
    flex: 1,
    width: '100%',
    minWidth: 280,
    order: 2,
    paddingBottom: Platform.OS === 'web' ? 0 : 20,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        order: 1,
        maxWidth: '52%',
        minWidth: 350,
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.1)',
        borderRadius: 16,
        padding: 12,
        marginRight: 12,
        paddingBottom: 0,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        minWidth: 400,
        padding: 16,
        marginRight: 16,
      }),
  },

  rightColumn: {
    flex: 1,
    width: '100%',
    minWidth: 250,
    order: 1,
    minHeight: 200,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        order: 2,
        maxWidth: '44%',
        minWidth: 280,
        paddingLeft: 12,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        minWidth: 300,
        paddingLeft: 16,
      }),
  },

  previewWrapper: {
    width: '100%',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        maxWidth: '100%',
      }),
  },

  // Panel Headers
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Platform.OS === 'web' ? 16 : 20,
    paddingBottom: Platform.OS === 'web' ? 12 : 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
    paddingHorizontal: Platform.OS === 'web' ? 0 : 4,
  },
  panelHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: Platform.OS === 'web' ? 12 : 12,
  },
  panelIconContainer: {
    width: Platform.OS === 'web' ? 36 : 40,
    height: Platform.OS === 'web' ? 36 : 40,
    borderRadius: Platform.OS === 'web' ? 10 : 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  panelTitle: {
    fontSize: Platform.OS === 'web' ? 20 : 20,
    fontWeight: '700',
    marginLeft: Platform.OS === 'web' ? 12 : 12,
    flex: 1,
    letterSpacing: Platform.OS === 'web' ? -0.3 : 0,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        fontSize: 22,
      }),
  },

  leftScrollContent: {
    flex: 1,
    paddingBottom: Platform.OS === 'web' ? 0 : 20,
  },

  leftSaveSection: {
    padding: 16,
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },

  topSaveSection: {
    padding: 16,
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },

  // Content Cards
  contentCard: {
    borderRadius: Platform.OS === 'web' ? 12 : 16,
    padding: Platform.OS === 'web' ? 8 : 16,
    marginBottom: Platform.OS === 'web' ? 4 : 16,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        borderRadius: 16,
        padding: 12,
        marginBottom: 6,
      }),
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 1200 && {
        borderRadius: 20,
        padding: 16,
        marginBottom: 8,
      }),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Platform.OS === 'web' ? 8 : 16,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardHeaderBadge: {
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
    paddingHorizontal: Platform.OS === 'web' ? 8 : 10,
    paddingVertical: Platform.OS === 'web' ? 4 : 6,
    borderRadius: Platform.OS === 'web' ? 12 : 14,
  },
  cardHeaderBadgeText: {
    fontSize: Platform.OS === 'web' ? 12 : 13,
    fontWeight: '700',
  },
  cardTitle: {
    fontSize: Platform.OS === 'web' ? 14 : 16,
    fontWeight: '700',
    marginLeft: Platform.OS === 'web' ? 8 : 10,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        fontSize: 16,
      }),
  },

  // Input Groups
  inputGroup: {
    marginBottom: Platform.OS === 'web' ? 4 : 20,
  },
  inputLabel: {
    fontSize: Platform.OS === 'web' ? 12 : 14,
    fontWeight: '700',
    marginBottom: Platform.OS === 'web' ? 4 : 8,
    opacity: Platform.OS === 'web' ? 0.8 : 1,
  },
  inputLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.OS === 'web' ? 8 : 12,
  },
  inputRequired: {
    fontSize: Platform.OS === 'web' ? 14 : 15,
    fontWeight: '700',
    marginLeft: 6,
  },
  inputOptional: {
    fontSize: Platform.OS === 'web' ? 12 : 12,
    fontWeight: '500',
    marginLeft: Platform.OS === 'web' ? 8 : 10,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  modernInput: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 14,
  },
  textArea: {
    height: 50,
    textAlignVertical: 'top',
  },
  displayText: {
    fontSize: 16,
    lineHeight: 24,
  },

  // Title Section
  titleSection: {
    marginTop: Platform.OS === 'web' ? 16 : 0,
    marginBottom: Platform.OS === 'web' ? 16 : 0,
  },
  // Privacy Toggle
  privacySection: {
    marginTop: Platform.OS === 'web' ? 16 : 8,
    marginBottom: Platform.OS === 'web' ? 8 : 0,
  },
  privacyToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  privacyInfo: {
    flex: 1,
  },
  privacyDescription: {
    fontSize: 13,
    marginTop: 2,
    opacity: 0.7,
  },
  modernToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    padding: 2,
  },
  toggleKnob: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },

  // Modern Images
  modernImagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        gap: 12,
      }),
  },
  modernImageItem: {
    width: '100%',
    aspectRatio: 16 / 9,
    marginBottom: 8,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        width: '48%',
        aspectRatio: 1,
        marginBottom: 0,
      }),
  },
  modernImagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },

  // Empty States
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  emptyText: {
    fontSize: 14,
    marginTop: 12,
    opacity: 0.7,
  },

  // Modern Colors
  modernColorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-start',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        gap: 12,
      }),
  },
  modernColorItem: {
    alignItems: 'center',
    position: 'relative',
    marginBottom: 8,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        marginBottom: 0,
      }),
  },
  modernColorSwatch: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        width: 60,
        height: 60,
        borderRadius: 30,
      }),
  },
  modernColorCode: {
    fontSize: 12,
    fontWeight: '500',
  },

  // Photo Colors Styles
  photoColorsContainer: {
    gap: 8,
  },
  photoColorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  photoThumbnail: {
    width: 50,
    height: 50,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  photoThumbnailImage: {
    width: '100%',
    height: '100%',
  },
  photoColorSwatches: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    flex: 1,
  },
  photoColorSwatch: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedPhotoColor: {
    borderColor: palette.brightViolet,
    borderWidth: 3,
  },

  // Selected Colors Styles
  selectedColorsSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  selectedColorsHeader: {
    marginBottom: 12,
  },
  selectedColorsTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  selectedColorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectedColorItem: {
    alignItems: 'center',
    position: 'relative',
  },
  selectedColorSwatch: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  selectedColorCode: {
    fontSize: 10,
    fontWeight: '500',
  },
  selectedColorRemoveButton: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: palette.white,
    borderRadius: 8,
    padding: 1,
  },

  // Simple Tags Styles
  simpleTagInput: {
    marginBottom: 8,
  },
  simpleInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
  },
  simpleTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  simpleTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  simpleTagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  simpleRemoveTagButton: {
    padding: 1,
  },
  simpleEmptyText: {
    fontSize: 13,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  characterCount: {
    fontSize: 12,
    textAlign: 'right',
    marginTop: 4,
    marginRight: 4,
  },
  tagWarning: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
    fontWeight: '500',
  },
  tagInfo: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  modernRemoveButton: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: palette.white,
    borderRadius: 12,
    padding: 2,
  },

  // Modern Tags
  modernTagInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  modernAddTagButton: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
  },
  modernTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        gap: 8,
      }),
  },
  modernTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 6,
  },
  modernTagText: {
    fontSize: 13,
    fontWeight: '500',
  },
  modernRemoveTagButton: {
    padding: 2,
  },

  // Add Button
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addButtonText: {
    color: palette.white,
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  name: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
  },
  editTextArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  editAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  editAddButtonText: {
    color: palette.white,
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  colorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  colorItem: {
    alignItems: 'center',
    margin: 8,
    position: 'relative',
  },
  editColorSwatch: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: palette.lightGray,
  },
  colorCode: {
    fontSize: 12,
    fontWeight: '500',
  },
  removeButton: {
    position: 'absolute',
    top: -3,
    right: -3,
    backgroundColor: palette.white,
    borderRadius: 8,
  },
  tagInput: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  addTagButton: {
    width: 36,
    height: 36,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  editTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
  },
  editTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: palette.veryLightGray,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    margin: 4,
  },
  editTagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  removeTagButton: {
    marginLeft: 4,
  },
  privacyOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  editPrivacyDescription: {
    fontSize: 12,
    marginTop: 2,
  },
  editToggle: {
    width: 40,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
  },
  editToggleKnob: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
  },
  stat: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 2,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  editActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    flex: 1,
    marginHorizontal: 6,
    justifyContent: 'center',
  },
  deleteButton: {
    backgroundColor: palette.brightMatteRed,
    borderColor: palette.brightMatteRed,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  metaText: {
    fontSize: 12,
    marginBottom: 4,
  },
  modalContainer: {
    flex: 1,
  },
  modalResponsiveWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' &&
      Dimensions.get('window').width > 768 && {
        maxWidth: '75%',
        alignSelf: 'center',
        width: '100%',
      }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: palette.lightGray,
  },
  modalCancelText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorText: {
    marginTop: 12,
    fontSize: 16,
    color: palette.brightMatteRed,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: palette.brightViolet,
    borderRadius: 8,
  },
  retryText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
  },
  imagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  imageItem: {
    alignItems: 'center',
    margin: 8,
    position: 'relative',
  },
  imagePreview: {
    width: 100,
    height: 100,
    borderRadius: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: palette.lightGray,
  },
  editEmptyText: {
    fontSize: 12,
    fontWeight: '500',
  },
  prominentSaveButton: {
    padding: 12,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  prominentSaveText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  bottomSaveSection: {
    padding: 16,
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: palette.lightGray,
  },

  // Preview Card Styles
  previewCard: {
    borderRadius: 16,
    marginBottom: 20,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    overflow: 'hidden',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  previewTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
    flex: 1,
  },
  previewToggle: {
    padding: 4,
  },
  previewContent: {
    padding: 16,
  },

  // Story Header
  storyHeader: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  storyMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userInfo: {
    marginLeft: 8,
    flex: 1,
  },
  username: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 1,
  },
  timestamp: {
    fontSize: 10,
    opacity: 0.6,
  },

  // Story Content
  storyContent: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  storyTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 4,
  },
  storyDescription: {
    fontSize: 12,
    lineHeight: 16,
    opacity: 0.8,
  },

  // Color Palette Section
  colorPaletteSection: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  paletteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  paletteTitle: {
    fontSize: 14,
    fontWeight: '600',
  },

  colorPalette: {
    flexDirection: 'row',
    height: 40,
    marginBottom: 8,
    borderRadius: 8,
    overflow: 'hidden',
  },
  colorSwatch: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  firstColorSwatch: {
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
  },
  lastColorSwatch: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
  },
  colorHex: {
    fontSize: 8,
    fontWeight: '600',
    color: palette.white,
    textShadow: '0 1px 1px rgba(0,0,0,0.5)',
  },

  // Visual Story Section
  visualStorySection: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  visualStoryTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  imageGrid: {
    flexDirection: 'row',
    height: 120,
    gap: 2,
  },
  imageContainer: {
    width: '24%',
    aspectRatio: 1,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  storyImage: {
    width: '100%',
    height: '100%',
  },
  moreImagesOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moreImagesText: {
    color: palette.white,
    fontSize: 12,
    fontWeight: '600',
  },

  // Tags Section
  tagsSection: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  tagsTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  storyTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  storyTagText: {
    fontSize: 10,
    fontWeight: '500',
  },
  moreTagsText: {
    fontSize: 12,
    opacity: 0.6,
    alignSelf: 'center',
  },

  // Story Actions
  storyActions: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderTopWidth: 1,
    gap: 16,
  },
  previewActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 6,
  },
  actionText: {
    fontSize: 10,
    fontWeight: '500',
  },
  emptyImageContainer: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    borderStyle: 'dashed',
  },
  emptyImagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },

  // Privacy Indicator
  privacyIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 12,
  },
  privacyIndicatorText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },

  // Preview Controls Styles
  previewControls: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    marginBottom: 16,
  },
  previewControlRow: {
    marginBottom: 12,
  },
  visibilityToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  visibilityInfo: {
    flex: 1,
  },
  visibilityLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  visibilityDescription: {
    fontSize: 12,
    opacity: 0.7,
  },

  // Header Post Button
  headerPostButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 16 : 20,
    paddingVertical: Platform.OS === 'web' ? 8 : 12,
    borderRadius: Platform.OS === 'web' ? 8 : 12,
    marginLeft: 'auto',
    minHeight: Platform.OS === 'web' ? 36 : 48,
    minWidth: Platform.OS === 'web' ? 80 : 100,
    shadowOffset: Platform.OS === 'web' ? undefined : { width: 0, height: 2 },
    shadowOpacity: Platform.OS === 'web' ? undefined : 0.25,
    shadowRadius: Platform.OS === 'web' ? undefined : 4,
    elevation: Platform.OS === 'web' ? undefined : 4,
  },
  headerPostText: {
    color: palette.white,
    fontSize: Platform.OS === 'web' ? 14 : 15,
    fontWeight: '600',
    marginLeft: 6,
    letterSpacing: Platform.OS === 'web' ? 0.2 : 0.2,
  },
});

export default MoodBoardDetailScreen;
